import type { SynthAttemptOutcome } from './audioContext';

type Reply = { v: 1; id: number; result: string; attempt?: number; epoch?: unknown };
export interface FillAudioPort {
  onmessage: ((event: MessageEvent<Reply>) => void) | null;
  postMessage(message: unknown): void;
  start(): void;
}
export interface FillAudioRequest {
  /** JSON tuple: authenticated user, org, role, credential mode, venue, fill. */
  key: string;
  valid(): boolean;
  ready(): boolean;
  attempt(canStart: () => boolean): SynthAttemptOutcome;
}
export type CoordinationResult = 'fallback' | 'suppressed' | 'expired' | 'attempted' | 'unknown' | 'capacity';
/** Whether this tab can coordinate fill sounds. Not set by one transient 'unknown'.
 * `replacement-failed`: a reconnect got no answer; a later same-epoch answer clears it.
 * `degraded`: lost or unknown ledger history; sticky until reload. */
export type FillAudioHealth = 'ok' | 'replacement-failed' | 'degraded';
const REQUEST_MS = 1000;
const LOCAL_PENDING_CAP = 1024;
const RETRY_MIN_MS = 1000;
const RETRY_MAX_MS = 30_000;

/** One port to the worker. Replies resolve only ids pending on their own
 * generation, so nothing crosses from an abandoned connection to a new one. */
interface Generation {
  port: FillAudioPort;
  sequence: number;
  pending: Map<number, (reply: Reply | null) => void>;
  /** No new claim goes out on a retired generation. Its own pending claims drain. */
  retired: boolean;
  probe: Promise<void> | null;
}

/** No timers/port registry in the worker per tab, and no broadcasts of account
 * identity. A timed-out claim is ambiguous: it must never fall back to playback.
 *
 * A claim reply is awaited for as long as the fill itself stays valid (the
 * caller's 30 s queued-age rule), not for a fixed second. A busy main thread
 * (a synchronous re-render of the frames published with the fill) can handle
 * the reply well after one second. The worker never re-grants a pending key,
 * so a late grant is still exclusive: peers that claimed meanwhile see `wait`
 * and then `uncertain`, and stay silent. Discarding that grant only silenced
 * the fill in every tab.
 *
 * #843 recovery (option A). A claim with no reply after REQUEST_MS sends one
 * hello probe on the same port; a live port answers the claim first (FIFO). If
 * the probe is silent too, the tab opens one new port, shared by every caller.
 * The worker's ledger is memory-only and the page cannot see it die, so the new
 * port is trusted only when its hello carries the same non-empty epoch as the
 * ledger this tab already used. A changed or missing epoch means lost history:
 * the tab stays degraded (every request 'unknown', no claim) until reload. It
 * never returns to 'fallback' after coordinating. The old port drains: a still-
 * valid grant for a claim it sent plays once and reports its outcome there;
 * the grant stays exclusive on the ledger that issued it (Oct 9 amendment).
 * Retirement precedes epoch verification, so this drain can finish after
 * degradation too. A fresh tab on a replacement ledger retains the existing
 * fresh-load duplicate limitation; no recovery claim is sent there by this tab.
 */
export class FillAudioCoordinator {
  private current: Generation | null = null;
  private opening: Promise<boolean> | null = null;
  private reconnecting: Promise<void> | null = null;
  private trustedEpoch: string | null = null;
  private degraded = false;
  private coolingDown = false;
  private retryMs = RETRY_MIN_MS;
  private inflight = 0;
  private health: FillAudioHealth = 'ok';
  constructor(private factory: () => FillAudioPort, private onHealth?: (health: FillAudioHealth) => void) {}

  private setHealth(next: FillAudioHealth): void {
    if (this.health === next || this.health === 'degraded') return;
    this.health = next;
    this.onHealth?.(next);
  }

  private connect(): Generation {
    const gen: Generation = { port: this.factory(), sequence: 0, pending: new Map(), retired: false, probe: null };
    gen.port.onmessage = event => {
      const reply = event.data;
      if (reply?.v === 1) gen.pending.get(reply.id)?.(reply);
    };
    gen.port.start();
    return gen;
  }

  /** `alive` extends the wait past REQUEST_MS while it holds; it is checked
   * once per REQUEST_MS, so an rpc never outlives its request by more than that.
   * `slow` runs once, when the first REQUEST_MS passes without a reply. */
  private rpc(gen: Generation, message: Record<string, unknown>, alive?: () => boolean,
    slow?: () => void): Promise<Reply | null> {
    // A full claim backlog is not evidence that a port failed to answer hello.
    // Opening, probing and reconnecting already serialize their control RPCs;
    // let those bounded hellos through without lifting the cap on fill traffic.
    if (message.type !== 'hello' && this.inflight >= LOCAL_PENDING_CAP) return Promise.resolve(null);
    const id = ++gen.sequence;
    this.inflight++;
    return new Promise(resolve => {
      let timer: ReturnType<typeof setTimeout>;
      let extended = false;
      const arm = () => {
        timer = setTimeout(() => {
          // No reply in REQUEST_MS is the port signal, even if this request has
          // expired meanwhile (a late timer in a hidden or sleeping tab).
          if (!extended) { extended = true; slow?.(); }
          if (alive?.()) arm(); else finish(null);
        }, REQUEST_MS);
      };
      const finish = (reply: Reply | null) => {
        if (!gen.pending.has(id)) return;
        clearTimeout(timer); gen.pending.delete(id); this.inflight--; resolve(reply);
      };
      gen.pending.set(id, finish);
      arm();
      try { gen.port.postMessage({ ...message, v: 1, id }); } catch { finish(null); }
    });
  }

  private static epochOf(reply: Reply | null): string | null {
    const epoch = reply?.epoch;
    return typeof epoch === 'string' && epoch.length > 0 && epoch.length <= 128 ? epoch : null;
  }

  private open(): Promise<boolean> {
    if (this.opening) return this.opening;
    this.opening = (async () => {
      try {
        const gen = this.connect();
        const reply = await this.rpc(gen, { type: 'hello' });
        if (reply?.result !== 'ready') return false;
        this.current = gen;
        this.trustedEpoch = FillAudioCoordinator.epochOf(reply);
        return true;
      } catch { return false; }
    })();
    return this.opening;
  }

  /** One probe per generation at a time. A silent probe retires the
   * generation and starts the one shared reconnect. */
  private suspect(gen: Generation): void {
    if (gen.retired || gen.probe || gen !== this.current) return;
    gen.probe = this.rpc(gen, { type: 'hello' }).then(reply => {
      gen.probe = null;
      if (reply?.result === 'ready' || gen.retired) return;
      gen.retired = true;
      if (!this.degraded) void this.reconnect();
    });
  }

  private reconnect(): Promise<void> {
    this.reconnecting ??= (async () => {
      let gen: Generation | null = null;
      let reply: Reply | null = null;
      try {
        gen = this.connect();
        reply = await this.rpc(gen, { type: 'hello' });
      } catch { reply = null; }
      if (gen && reply?.result === 'ready') {
        const epoch = FillAudioCoordinator.epochOf(reply);
        if (epoch !== null && epoch === this.trustedEpoch) {
          this.current = gen;
          this.retryMs = RETRY_MIN_MS;
          this.setHealth('ok');
        } else {
          // Lost or unknown history: W1's records may hold a fill a peer played.
          this.degraded = true;
          gen.retired = true;
          this.setHealth('degraded');
        }
      } else {
        // Nothing answered. A late hello on this port resolves nothing (its id
        // is no longer pending) and the port never carries a claim.
        if (gen) gen.retired = true;
        this.coolingDown = true;
        setTimeout(() => { this.coolingDown = false; }, this.retryMs);
        this.retryMs = Math.min(RETRY_MAX_MS, this.retryMs * 2);
        this.setHealth('replacement-failed');
      }
    })().finally(() => { this.reconnecting = null; });
    return this.reconnecting;
  }

  /** The generation a new claim may use, or null while none is usable. */
  private async usable(): Promise<Generation | null> {
    for (;;) {
      if (this.degraded) return null;
      const gen = this.current;
      if (gen && !gen.retired) return gen;
      if (this.reconnecting) { await this.reconnecting; continue; }
      if (this.coolingDown) return null;
      await this.reconnect();
      if (this.reconnecting) continue;
      const after = this.current;
      return after && !after.retired && !this.degraded ? after : null;
    }
  }

  async request(request: FillAudioRequest): Promise<CoordinationResult> {
    if (!await this.open()) return 'fallback'; // no claim was sent
    if (!request.valid() || !request.ready()) return 'expired';
    const gen = await this.usable();
    if (!gen) return 'unknown';
    // A request is pinned to the generation of its first claim.
    while (request.valid() && request.ready()) {
      if (gen.retired) return 'unknown';
      const reply = await this.rpc(gen, { type: 'claim', key: request.key }, () => request.valid(),
        () => this.suspect(gen));
      // A tab frozen past the fill's validity may still receive a queued
      // grant. It must not play, and it does not release the grant: the fill
      // stays consumed, so no peer replays it late either.
      if (!reply || !request.valid()) return 'unknown';
      if (reply.result === 'suppress') return 'suppressed';
      // A dead owner's tab cannot report its own sound health. Let a surviving
      // peer surface the ambiguity without granting it permission to replay.
      if (reply.result === 'uncertain') return 'unknown';
      if (reply.result === 'capacity') return 'capacity';
      if (reply.result === 'wait') {
        await new Promise(resolve => setTimeout(resolve, 50));
        continue;
      }
      if (reply.result !== 'grant' || !Number.isSafeInteger(reply.attempt)) return 'unknown';
      let outcome: SynthAttemptOutcome | { kind: 'unknown' };
      try {
        outcome = request.attempt(() => request.valid() && request.ready());
      } catch { outcome = { kind: 'unknown' }; }
      // No reply is needed to permit another sound. Only a confirmed zero-start
      // report can release this attempt; all other outcomes remain consumed.
      // The outcome goes to the generation that granted it (the worker matches
      // the owning port), even if that generation is draining.
      void this.rpc(gen, { type: 'outcome', key: request.key, attempt: reply.attempt, outcome: outcome.kind });
      return outcome.kind === 'unknown' ? 'unknown' : 'attempted';
    }
    return 'expired';
  }
}

let singleton: FillAudioCoordinator | null = null;
let tabHealth: FillAudioHealth = 'ok';
const healthListeners = new Set<() => void>();
/** This tab's fill-sound coordination health, for the status-bar chip. */
export function getFillAudioHealth(): FillAudioHealth { return tabHealth; }
export function subscribeFillAudioHealth(listener: () => void): () => void {
  healthListeners.add(listener);
  return () => { healthListeners.delete(listener); };
}
export function coordinateFillAudio(request: FillAudioRequest): Promise<CoordinationResult> | null {
  if (typeof SharedWorker === 'undefined') return null;
  singleton ??= new FillAudioCoordinator(() => new SharedWorker('/fill-audio-coordinator-v1.js',
    { name: 'g1-fill-audio-v1' }).port as FillAudioPort, health => {
    tabHealth = health;
    healthListeners.forEach(listener => listener());
  });
  return singleton.request(request);
}
